/**
 * /admin/teams — team catalog browse + rename.
 *
 * Founder ask (2026-07-31): franchises change ownership and rebrand, so
 * an ingested team's name has to be correctable after the fact. This is
 * the only surface that can do it — the customer-facing
 * /api/coredata/teams/ PATCH refuses SYSTEM / locked rows outright, and
 * every ingested team is exactly that.
 *
 * Renaming appends a TeamNameHistory row (year + optional reason) so the
 * old name survives the in-place update; past names are searchable and
 * shown inline.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  RefreshCw, ArrowLeft, Loader2, Pencil, History, AlertTriangle,
  CheckCircle2, X, Search,
} from "lucide-react";
import {
  listAdminTeams, getAdminTeam, updateAdminTeam,
} from "../../api/owner.api";
import DialogShell from "../../components/common/DialogShell";
import Pagination from "../../components/common/Pagination";

const PAGE_SIZE = 25; // OwnerPagination.page_size

const OWNER_OPTIONS = [
  { value: "SYSTEM", label: "Shared catalog (SYSTEM)" },
  { value: "USER",   label: "Customer-owned (USER)" },
  { value: "ORG",    label: "Org-owned (ORG)" },
  { value: "all",    label: "All owners" },
];

const ARCHIVAL_OPTIONS = [
  { value: "false", label: "Live catalog only" },
  { value: "true",  label: "Archive-only teams" },
  { value: "all",   label: "Both" },
];

export default function AdminTeams() {
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [ownerType, setOwnerType] = useState("SYSTEM");
  const [archival, setArchival] = useState("false");
  const [renamedOnly, setRenamedOnly] = useState(false);

  const [editing, setEditing] = useState(null); // the team row being renamed
  const [toast, setToast] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await listAdminTeams({
        search: search || undefined,
        owner_type: ownerType,
        archival,
        renamed: renamedOnly ? "true" : undefined,
        page,
      });
      setRows(data.results || []);
      setCount(data.count || 0);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Failed to load teams.");
      setRows([]);
      setCount(0);
    } finally {
      setLoading(false);
    }
  }, [search, ownerType, archival, renamedOnly, page]);

  useEffect(() => { load(); }, [load]);

  // Any filter change invalidates the current page number.
  useEffect(() => { setPage(1); }, [search, ownerType, archival, renamedOnly]);

  function onSaved(updated, message) {
    setEditing(null);
    setToast(message);
    setRows((prev) => prev.map((r) => (r.id === updated.id ? { ...r, ...updated } : r)));
  }

  return (
    <div className="p-6 space-y-4">
      <Link
        to="/admin"
        className="inline-flex items-center gap-1 text-sm text-text-secondary hover:text-text-primary"
      >
        <ArrowLeft className="w-4 h-4" /> Back to dashboard
      </Link>

      <header className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Teams</h1>
          <p className="text-sm text-text-secondary mt-1">
            {count.toLocaleString("en-IN")} team(s). Rename a franchise here when
            it changes hands — the old name is kept in the team's history.
          </p>
        </div>
        <button
          onClick={load}
          className="px-3 py-2 text-sm rounded-md border border-border-default hover:bg-bg-secondary flex items-center gap-1.5"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </header>

      <div className="flex items-center gap-3 flex-wrap text-sm">
        <form
          onSubmit={(e) => { e.preventDefault(); setSearch(searchInput.trim()); }}
          className="relative"
        >
          <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-text-secondary" />
          <input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Name, short name, Cricinfo id, or a past name…"
            aria-label="Search teams"
            className="pl-8 pr-3 py-1.5 rounded-md border border-border-default bg-bg-primary w-80"
          />
        </form>
        <select
          value={ownerType}
          onChange={(e) => setOwnerType(e.target.value)}
          aria-label="Owner type"
          className="px-3 py-1.5 rounded-md border border-border-default bg-bg-primary"
        >
          {OWNER_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <select
          value={archival}
          onChange={(e) => setArchival(e.target.value)}
          aria-label="Archive filter"
          className="px-3 py-1.5 rounded-md border border-border-default bg-bg-primary"
        >
          {ARCHIVAL_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <label className="flex items-center gap-1.5 text-text-secondary">
          <input
            type="checkbox"
            checked={renamedOnly}
            onChange={(e) => setRenamedOnly(e.target.checked)}
          />
          Renamed only
        </label>
      </div>

      {toast && (
        <div className="rounded-md bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 px-4 py-2 text-sm flex items-start justify-between gap-3">
          <span className="flex items-start gap-1.5">
            <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
            <span className="whitespace-pre-line">{toast}</span>
          </span>
          <button onClick={() => setToast("")} aria-label="Dismiss">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {error && (
        <div className="rounded-md bg-red-500/10 border border-red-500/30 text-red-300 px-4 py-2 text-sm flex items-center gap-1.5">
          <AlertTriangle className="w-4 h-4" /> {error}
        </div>
      )}

      <div className="rounded-md border border-border-default bg-bg-primary overflow-x-auto">
        <table className="w-full text-sm">
          <caption className="sr-only">Teams matching the current filters</caption>
          <thead className="bg-bg-secondary text-left text-xs uppercase tracking-wider text-text-secondary">
            <tr>
              <th scope="col" className="px-4 py-2.5">Team</th>
              <th scope="col" className="px-4 py-2.5">Short</th>
              <th scope="col" className="px-4 py-2.5">Type</th>
              <th scope="col" className="px-4 py-2.5">Nationality</th>
              <th scope="col" className="px-4 py-2.5 text-right">Squad</th>
              <th scope="col" className="px-4 py-2.5 text-right">Matches</th>
              <th scope="col" className="px-4 py-2.5">Owner</th>
              <th scope="col" className="px-4 py-2.5 w-24" />
            </tr>
          </thead>
          <tbody className="divide-y divide-border-default">
            {loading && rows.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-text-secondary">
                <Loader2 className="w-5 h-5 inline-block mr-2 animate-spin" /> Loading…
              </td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-text-secondary">
                No teams match these filters.
              </td></tr>
            )}
            {rows.map((t) => (
              <tr key={t.id} className="hover:bg-bg-secondary">
                <td className="px-4 py-2 font-medium">
                  <div className="flex items-center gap-2">
                    <span>{t.name}</span>
                    {t.is_archival && (
                      <span className="px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wide bg-amber-500/10 text-amber-300 border border-amber-500/30">
                        archive
                      </span>
                    )}
                    {t.rename_count > 0 && (
                      <span
                        title={`${t.rename_count} rename(s) logged`}
                        className="inline-flex items-center gap-0.5 text-[10px] text-text-secondary"
                      >
                        <History className="w-3 h-3" /> {t.rename_count}
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-2 font-mono text-xs">{t.short_name || "—"}</td>
                <td className="px-4 py-2 text-xs text-text-secondary">{t.team_type}</td>
                <td className="px-4 py-2 text-xs text-text-secondary">
                  {t.nationality_name || "—"}
                </td>
                <td className="px-4 py-2 text-right tabular-nums">{t.squad_count}</td>
                <td className="px-4 py-2 text-right tabular-nums">{t.match_count}</td>
                <td className="px-4 py-2 text-xs text-text-secondary">{t.owner_type}</td>
                <td className="px-4 py-2 text-right">
                  <button
                    onClick={() => setEditing(t)}
                    className="px-2.5 py-1 text-xs rounded-md border border-border-default hover:bg-bg-secondary inline-flex items-center gap-1"
                  >
                    <Pencil className="w-3.5 h-3.5" /> Rename
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Pagination count={count} page={page} onPage={setPage} pageSize={PAGE_SIZE} />

      <RenameDialog
        team={editing}
        onClose={() => setEditing(null)}
        onSaved={onSaved}
      />
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════ */

function RenameDialog({ team, onClose, onSaved }) {
  const [name, setName] = useState("");
  const [shortName, setShortName] = useState("");
  const [year, setYear] = useState("");
  const [reason, setReason] = useState("");
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!team) return;
    setName(team.name || "");
    setShortName(team.short_name || "");
    setYear(String(new Date().getFullYear()));
    setReason("");
    setErr("");
    setHistory([]);
    // Fetch the full history — the list row only carries a count.
    getAdminTeam(team.id)
      .then(({ data }) => setHistory(data.name_history || []))
      .catch(() => { /* history is supplementary; the form still works */ });
  }, [team]);

  if (!team) return null;

  const nameChanged = name.trim() !== team.name;
  const shortChanged = shortName.trim() !== (team.short_name || "");
  const dirty = nameChanged || shortChanged;

  async function save() {
    if (!dirty || busy) return;
    setBusy(true);
    setErr("");
    try {
      const { data } = await updateAdminTeam(team.id, {
        name: name.trim(),
        shortName: shortName.trim(),
        changeYear: nameChanged && year ? Number(year) : undefined,
        changeReason: nameChanged ? reason : undefined,
      });
      const msg = data.renamed
        ? `Renamed to "${data.team.name}". ${data.ingest_warning}`
        : `Updated "${data.team.name}".`;
      onSaved(data.team, msg);
    } catch (e) {
      const body = e?.response?.data;
      setErr(body?.detail || e.message || "Save failed.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <DialogShell
      open={!!team}
      onClose={onClose}
      title={`Rename — ${team.name}`}
      size="xl"
    >
      <div className="p-5 space-y-4 text-sm">
        <div className="space-y-1.5">
          <label htmlFor="team-name" className="block text-xs uppercase tracking-wider text-text-secondary">
            Team name
          </label>
          <input
            id="team-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={150}
            className="w-full px-3 py-2 rounded-md border border-border-default bg-bg-main"
          />
        </div>

        <div className="space-y-1.5">
          <label htmlFor="team-short" className="block text-xs uppercase tracking-wider text-text-secondary">
            Short name
          </label>
          <input
            id="team-short"
            value={shortName}
            onChange={(e) => setShortName(e.target.value)}
            maxLength={20}
            className="w-full px-3 py-2 rounded-md border border-border-default bg-bg-main font-mono"
          />
        </div>

        {nameChanged && (
          <>
            <div className="grid grid-cols-[8rem_1fr] gap-3">
              <div className="space-y-1.5">
                <label htmlFor="team-year" className="block text-xs uppercase tracking-wider text-text-secondary">
                  Change year
                </label>
                <input
                  id="team-year"
                  type="number"
                  min={1700}
                  max={2200}
                  value={year}
                  onChange={(e) => setYear(e.target.value)}
                  className="w-full px-3 py-2 rounded-md border border-border-default bg-bg-main"
                />
              </div>
              <div className="space-y-1.5">
                <label htmlFor="team-reason" className="block text-xs uppercase tracking-wider text-text-secondary">
                  Reason (optional)
                </label>
                <input
                  id="team-reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="e.g. Franchise sold to new owners"
                  className="w-full px-3 py-2 rounded-md border border-border-default bg-bg-main"
                />
              </div>
            </div>

            <div className="rounded-md bg-amber-500/10 border border-amber-500/30 text-amber-200 px-3 py-2 text-xs flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>
                Ingest matches teams by exact name. A future upload that still
                says <strong>{team.name}</strong> will create a new archive team
                instead of reusing this one — you'll need to merge it by hand.
              </span>
            </div>
          </>
        )}

        {err && (
          <div className="rounded-md bg-red-500/10 border border-red-500/30 text-red-300 px-3 py-2 text-xs flex items-start gap-1.5">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> {err}
          </div>
        )}

        {history.length > 0 && (
          <div className="space-y-1.5">
            <h3 className="text-xs uppercase tracking-wider text-text-secondary flex items-center gap-1.5">
              <History className="w-3.5 h-3.5" /> Previous names
            </h3>
            <ul className="divide-y divide-border-default rounded-md border border-border-default">
              {history.map((h) => (
                <li key={h.id} className="px-3 py-2 text-xs">
                  <span className="font-medium">{h.previous_name}</span>
                  {" → "}
                  <span className="font-medium">{h.new_name}</span>
                  <span className="text-text-secondary"> · {h.change_year}</span>
                  {h.change_reason && (
                    <div className="text-text-secondary mt-0.5">{h.change_reason}</div>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div className="px-5 py-3 border-t border-border-default flex justify-end gap-2">
        <button
          onClick={onClose}
          className="px-4 py-2 text-sm rounded-md border border-border-default hover:bg-bg-secondary"
        >
          Cancel
        </button>
        <button
          onClick={save}
          disabled={!dirty || busy}
          className="px-4 py-2 text-sm rounded-md bg-button-primary text-white disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
        >
          {busy && <Loader2 className="w-4 h-4 animate-spin" />}
          Save
        </button>
      </div>
    </DialogShell>
  );
}
